import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { AccessibleCompany } from '@/lib/api/common/interfaces/user.interface'
import { ApiResponse } from '@/lib/api/request'
import {
    importData,
    importFromFile,
    ShareableModule,
    ShareResultDto,
} from '@/lib/api/share/share-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { useAppSelector } from '@/store/hooks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    MdApartment,
    MdArrowBack,
    MdBusiness,
    MdCheck,
    MdChecklist,
    MdClose,
    MdCloudUpload,
    MdCode,
    MdDataObject,
    MdDescription,
    MdError,
    MdFileDownload,
    MdInfo,
    MdInsertDriveFile,
    MdPictureAsPdf,
    MdSearch,
    MdSelectAll,
    MdTableChart,
    MdUploadFile,
} from 'react-icons/md'
import { toast } from 'react-toastify'

const MODULES_WITH_PARTNER_MARKUP: ShareableModule[] = [ShareableModule.CATALOG]

const PAGE_LIMIT = 15

type ImportMode = 'all' | 'select' | 'search'
type ImportSource = 'unit' | 'file'
type Step = 'source' | 'company' | 'mode' | 'select' | 'file' | 'result'

interface FileFormat {
    name: string
    extension: string
    description: string
    icon: React.ReactNode
}

const FILE_FORMATS: FileFormat[] = [
    {
        name: 'CSV',
        extension: '.csv',
        description:
            'Planilha separada por virgulas. Formato mais comum e compativel com Excel, Google Sheets e outros sistemas.',
        icon: <MdTableChart className="text-xl text-success" />,
    },
    {
        name: 'Excel (XLSX)',
        extension: '.xlsx',
        description:
            'Planilha Microsoft Excel. Suporta multiplas abas, formatacao e formulas.',
        icon: <MdTableChart className="text-xl text-green-600" />,
    },
    {
        name: 'Excel (XLS)',
        extension: '.xls',
        description:
            'Formato antigo do Excel (97-2003). Compativel com sistemas legados.',
        icon: <MdTableChart className="text-xl text-green-700" />,
    },
    {
        name: 'PDF',
        extension: '.pdf',
        description:
            'Documento PDF com tabelas. Extraido automaticamente via IA.',
        icon: <MdPictureAsPdf className="text-xl text-error" />,
    },
    {
        name: 'JSON',
        extension: '.json',
        description:
            'Formato de dados estruturado. Usado por APIs e sistemas modernos.',
        icon: <MdDataObject className="text-xl text-warning" />,
    },
    {
        name: 'XML',
        extension: '.xml',
        description:
            'Formato padrao para troca de dados entre sistemas. Usado por NFe, TISS e outros.',
        icon: <MdCode className="text-xl text-info" />,
    },
    {
        name: 'TXT (Tabulado)',
        extension: '.txt',
        description:
            'Arquivo de texto separado por tabulacao. Exportado por muitos sistemas ERP e clinicos.',
        icon: <MdDescription className="text-xl text-base-content/70" />,
    },
]

interface ImportDataModalProps {
    isOpen: boolean
    onClose: () => void
    module: ShareableModule
    moduleLabel: string
    /**
     * Carregamento completo (sem paginação). Use apenas para módulos com
     * volume pequeno. Para módulos com muitos registros, use `fetchPaginated`.
     */
    fetchItems?: (companyId: number) => Promise<any[]>
    /**
     * Carregamento paginado + busca server-side. Recomendado para módulos
     * com volume grande (ex: Clientes). Tem prioridade sobre `fetchItems`.
     */
    fetchPaginated?: (
        companyId: number,
        params: { page?: number; limit?: number; search?: string }
    ) => Promise<ApiResponse<any[]>>
    renderItem: (item: any) => React.ReactNode
    getItemId: (item: any) => number
    getItemName: (item: any) => string
    onImportComplete?: () => void
    hideFileImport?: boolean
}

export function ImportDataModal({
    isOpen,
    onClose,
    module,
    moduleLabel,
    fetchItems,
    fetchPaginated,
    renderItem,
    getItemId,
    getItemName,
    onImportComplete,
    hideFileImport,
}: ImportDataModalProps) {
    const { user, accessibleCompanies } = useAppSelector(state => state.auth)

    const isPaginated = !!fetchPaginated

    const [step, setStep] = useState<Step>('source')
    const [selectedCompany, setSelectedCompany] =
        useState<AccessibleCompany | null>(null)
    const [items, setItems] = useState<any[]>([])
    const [itemsTotal, setItemsTotal] = useState(0)
    const [loadingItems, setLoadingItems] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [page, setPage] = useState(1)
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [importing, setImporting] = useState(false)
    const [result, setResult] = useState<ShareResultDto | null>(null)
    const [importMode, setImportMode] = useState<ImportMode | null>(null)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [partnerId, setPartnerId] = useState<number | null>(null)
    const [defaultMarkup, setDefaultMarkup] = useState<string>('')
    const fileInputRef = useRef<HTMLInputElement>(null)

    const debouncedSearch = useDebounce(searchTerm, 300)

    const showPartnerMarkup = MODULES_WITH_PARTNER_MARKUP.includes(module)

    const availableCompanies = accessibleCompanies.filter(
        c => c.id !== user?.companyId
    )

    useEffect(() => {
        if (isOpen) {
            setStep(hideFileImport ? 'company' : 'source')
            setSelectedCompany(null)
            setItems([])
            setItemsTotal(0)
            setHasMore(false)
            setPage(1)
            setSelectedIds([])
            setSearchTerm('')
            setResult(null)
            setImportMode(null)
            setSelectedFile(null)
            setPartnerId(null)
            setDefaultMarkup('')
        }
    }, [isOpen, hideFileImport])

    const filteredItems = useMemo(() => {
        if (isPaginated) return items
        if (!searchTerm.trim()) return items
        const term = searchTerm.toLowerCase()
        return items.filter(item =>
            getItemName(item).toLowerCase().includes(term)
        )
    }, [items, searchTerm, getItemName, isPaginated])

    const loadPage = useCallback(
        async (
            companyId: number,
            targetPage: number,
            append: boolean,
            search: string
        ) => {
            if (!fetchPaginated) return
            if (append) setLoadingMore(true)
            else setLoadingItems(true)
            try {
                const response = await fetchPaginated(companyId, {
                    page: targetPage,
                    limit: PAGE_LIMIT,
                    search: search || undefined,
                })
                if (response.success) {
                    const newItems = response.result || []
                    setItems(prev =>
                        append ? [...prev, ...newItems] : newItems
                    )
                    setItemsTotal(response.total ?? newItems.length)
                    setHasMore(
                        response.pagination ? !response.pagination.end : false
                    )
                    setPage(targetPage)
                }
            } catch (error) {
                console.error('Erro ao carregar itens:', error)
            } finally {
                setLoadingItems(false)
                setLoadingMore(false)
            }
        },
        [fetchPaginated]
    )

    useEffect(() => {
        if (!isPaginated) return
        if (step !== 'select') return
        if (!selectedCompany) return
        loadPage(selectedCompany.id, 1, false, debouncedSearch.trim())
    }, [debouncedSearch, isPaginated, step, selectedCompany, loadPage])

    const handleSelectSource = (source: ImportSource) => {
        if (source === 'unit') {
            setStep('company')
        } else {
            setStep('file')
        }
    }

    const handleSelectCompany = async (company: AccessibleCompany) => {
        setSelectedCompany(company)
        setLoadingItems(true)
        try {
            if (fetchPaginated) {
                const response = await fetchPaginated(company.id, {
                    page: 1,
                    limit: PAGE_LIMIT,
                })
                if (response.success) {
                    const data = response.result || []
                    setItems(data)
                    setItemsTotal(response.total ?? data.length)
                    setHasMore(
                        response.pagination ? !response.pagination.end : false
                    )
                    setPage(1)
                }
            } else if (fetchItems) {
                const data = await fetchItems(company.id)
                setItems(data || [])
                setItemsTotal((data || []).length)
                setHasMore(false)
            }
        } catch (error) {
            console.error('Erro ao carregar itens:', error)
            setItems([])
            setItemsTotal(0)
        } finally {
            setLoadingItems(false)
        }
        setStep('mode')
    }

    const handleSelectMode = (mode: ImportMode) => {
        setImportMode(mode)
        if (mode === 'all') {
            handleImport()
        } else {
            setSearchTerm('')
            setSelectedIds([])
            setStep('select')
        }
    }

    const handleLoadMore = () => {
        if (!isPaginated || !selectedCompany || loadingMore || !hasMore) return
        loadPage(selectedCompany.id, page + 1, true, debouncedSearch.trim())
    }

    const handleToggleItem = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        )
    }

    const handleToggleAll = () => {
        const visibleIds = filteredItems.map(getItemId)
        const allSelected = visibleIds.every(id => selectedIds.includes(id))
        if (allSelected) {
            setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)))
        } else {
            setSelectedIds(prev => [
                ...prev,
                ...visibleIds.filter(id => !prev.includes(id)),
            ])
        }
    }

    const handleImport = async (ids?: number[]) => {
        if (!selectedCompany) return

        setImporting(true)
        try {
            const response = await importData({
                module,
                sourceCompanyId: selectedCompany.id,
                recordIds: ids,
            })

            if (response.success && response.result) {
                setResult(response.result)
            }
        } catch (error) {
            console.error('Erro ao importar:', error)
        } finally {
            setImporting(false)
            setStep('result')
        }
    }

    const handleImportSelected = () => {
        if (selectedIds.length === 0) return
        handleImport(selectedIds)
    }

    const handleSelectFormat = (extension: string) => {
        const accept = extension
        if (fileInputRef.current) {
            fileInputRef.current.accept = accept
            fileInputRef.current.click()
        }
    }

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setSelectedFile(file)
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleFileImport = async () => {
        if (!selectedFile) return

        setImporting(true)
        try {
            const response = await importFromFile(selectedFile, module, {
                partnerId: partnerId || undefined,
                defaultMarkupPercentage: defaultMarkup
                    ? parseFloat(defaultMarkup)
                    : undefined,
            })
            if (response.success) {
                toast.info(
                    partnerId
                        ? 'Importação em andamento. Você será notificado para revisar os preços.'
                        : 'Importação em andamento. Você será notificado quando concluir.'
                )
                onImportComplete?.()
                onClose()
            } else {
                toast.error(response.error || 'Erro ao iniciar importação')
            }
        } catch (error) {
            console.error('Erro ao iniciar importação:', error)
            toast.error('Erro ao iniciar importação')
        } finally {
            setImporting(false)
        }
    }

    const handleClose = () => {
        if (result && result.results.some(r => r.success)) {
            onImportComplete?.()
        }
        onClose()
    }

    const handleBack = () => {
        if (step === 'company' && hideFileImport) {
            handleClose()
            return
        }
        if (step === 'company' || step === 'file') {
            setStep('source')
            setSelectedCompany(null)
            setItems([])
            setSelectedFile(null)
        } else if (step === 'mode') {
            setStep('company')
            setSelectedCompany(null)
            setItems([])
        } else if (step === 'select') {
            setStep('mode')
            setSelectedIds([])
            setSearchTerm('')
        }
    }

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    if (!isOpen) return null

    const successCount = result?.results.filter(r => r.success).length || 0
    const errorCount = result?.results.filter(r => !r.success).length || 0

    const selectedCount = selectedIds.length

    return (
        <div className="modal modal-open">
            <div className="modal-box !max-w-lg max-h-[85vh] flex flex-col">
                <button
                    className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    onClick={handleClose}
                >
                    <MdClose className="text-lg" />
                </button>

                <h3 className="font-bold text-lg flex items-center gap-2 mb-4">
                    <MdFileDownload className="text-secondary" />
                    Importar {moduleLabel}
                </h3>

                {step === 'source' && (
                    <div className="flex-1 overflow-y-auto">
                        <p className="text-sm text-base-content/70 mb-4">
                            De onde deseja importar?
                        </p>

                        <div className="space-y-3">
                            <button
                                type="button"
                                onClick={() => handleSelectSource('unit')}
                                className="flex items-center gap-4 p-4 rounded-xl w-full text-left bg-base-200 hover:bg-secondary/10 hover:border-secondary/30 border border-transparent transition-all"
                            >
                                <div className="p-3 bg-secondary/10 rounded-xl">
                                    <MdApartment className="text-2xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium">
                                        Importar de outra Unidade
                                    </p>
                                    <p className="text-xs text-base-content/60 mt-0.5">
                                        Copiar {moduleLabel.toLowerCase()} de
                                        outra unidade da sua organização
                                    </p>
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => handleSelectSource('file')}
                                className="flex items-center gap-4 p-4 rounded-xl w-full text-left bg-base-200 hover:bg-secondary/10 hover:border-secondary/30 border border-transparent transition-all"
                            >
                                <div className="p-3 bg-secondary/10 rounded-xl">
                                    <MdUploadFile className="text-2xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium">
                                        Importar de Arquivo
                                    </p>
                                    <p className="text-xs text-base-content/60 mt-0.5">
                                        Enviar arquivo CSV, Excel, PDF ou outros
                                        formatos
                                    </p>
                                </div>
                            </button>
                        </div>
                    </div>
                )}

                {step === 'file' && (
                    <div className="flex-1 overflow-y-auto">
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={handleFileSelected}
                        />

                        {selectedFile ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 p-4 bg-base-200 rounded-xl">
                                    <div className="p-3 bg-secondary/10 rounded-xl">
                                        <MdInsertDriveFile className="text-2xl text-secondary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">
                                            {selectedFile.name}
                                        </p>
                                        <p className="text-xs text-base-content/60">
                                            {formatFileSize(selectedFile.size)}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm btn-circle"
                                        onClick={() => setSelectedFile(null)}
                                        disabled={importing}
                                    >
                                        <MdClose className="text-lg" />
                                    </button>
                                </div>

                                <div className="alert alert-info">
                                    <MdInfo className="text-lg flex-shrink-0" />
                                    <span className="text-sm">
                                        O arquivo sera analisado por
                                        inteligencia artificial para identificar
                                        e importar {moduleLabel.toLowerCase()}{' '}
                                        automaticamente.
                                    </span>
                                </div>

                                {showPartnerMarkup && (
                                    <div className="space-y-3 p-4 bg-base-200 rounded-xl">
                                        <p className="text-xs font-medium text-base-content/70 uppercase tracking-wide">
                                            Configurações opcionais
                                        </p>

                                        <PartnerSelector
                                            value={partnerId}
                                            onChange={id => setPartnerId(id)}
                                            label="Parceiro"
                                            placeholder="Associar a um parceiro (opcional)"
                                            disabled={importing}
                                        />

                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text">
                                                    Porcentagem de markup padrão
                                                    (%)
                                                </span>
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="Ex: 30"
                                                className="input input-bordered w-full"
                                                value={defaultMarkup}
                                                onChange={e =>
                                                    setDefaultMarkup(
                                                        e.target.value
                                                    )
                                                }
                                                disabled={importing}
                                            />
                                            <label className="label">
                                                <span className="label-text-alt text-base-content/50">
                                                    Deixe em branco para não
                                                    aplicar.
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="btn btn-secondary w-full gap-2"
                                    onClick={handleFileImport}
                                    disabled={importing}
                                >
                                    {importing ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm" />
                                            Processando arquivo...
                                        </>
                                    ) : (
                                        <>
                                            <MdCloudUpload className="text-xl" />
                                            Importar arquivo
                                        </>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <>
                                <p className="text-sm font-medium mb-3">
                                    Selecione o formato do arquivo:
                                </p>

                                <div className="space-y-2">
                                    {FILE_FORMATS.map(format => (
                                        <button
                                            key={format.extension}
                                            type="button"
                                            onClick={() =>
                                                handleSelectFormat(
                                                    format.extension
                                                )
                                            }
                                            className="flex items-center gap-3 p-3 rounded-lg w-full text-left bg-base-200 hover:bg-base-300 transition-colors border border-transparent"
                                        >
                                            <div className="p-2 bg-base-100 rounded-lg flex-shrink-0">
                                                {format.icon}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-medium">
                                                        {format.name}
                                                    </p>
                                                    <span className="badge badge-xs badge-ghost">
                                                        {format.extension}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-base-content/60 mt-0.5 line-clamp-2">
                                                    {format.description}
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {step === 'company' && (
                    <div className="flex-1 overflow-y-auto">
                        {availableCompanies.length === 0 ? (
                            <div className="text-center py-8 text-base-content/60">
                                <MdBusiness className="text-4xl mx-auto mb-2" />
                                <p>Voce nao tem acesso a outras unidades.</p>
                            </div>
                        ) : (
                            <>
                                <p className="text-sm text-base-content/70 mb-3">
                                    Selecione a unidade de onde deseja importar:
                                </p>
                                <div className="space-y-2">
                                    {availableCompanies.map(company => (
                                        <button
                                            key={company.id}
                                            type="button"
                                            onClick={() =>
                                                handleSelectCompany(company)
                                            }
                                            className="flex items-center gap-3 p-3 rounded-lg w-full text-left bg-base-200 hover:bg-base-300 transition-colors"
                                        >
                                            <div className="avatar placeholder">
                                                <div className="bg-base-300 text-base-content rounded-full w-10 h-10 flex items-center justify-center text-sm font-medium">
                                                    {company.name
                                                        ?.charAt(0)
                                                        .toUpperCase() || 'E'}
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">
                                                    {company.tradingName ||
                                                        company.name}
                                                </p>
                                                {company.tradingName &&
                                                    company.name !==
                                                        company.tradingName && (
                                                        <p className="text-xs text-base-content/60 truncate">
                                                            {company.name}
                                                        </p>
                                                    )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {step === 'mode' && (
                    <div className="flex-1 overflow-y-auto">
                        {loadingItems ? (
                            <div className="flex flex-col items-center justify-center py-12 gap-3">
                                <span className="loading loading-spinner loading-lg text-secondary" />
                                <p className="text-sm text-base-content/60">
                                    Carregando itens...
                                </p>
                            </div>
                        ) : itemsTotal === 0 ? (
                            <div className="text-center py-8 text-base-content/60">
                                <MdFileDownload className="text-4xl mx-auto mb-2" />
                                <p>Nenhum item encontrado nessa unidade.</p>
                            </div>
                        ) : (
                            <>
                                <div className="mb-4 p-3 bg-base-200 rounded-lg">
                                    <p className="text-sm text-base-content/70">
                                        Importando de:
                                    </p>
                                    <p className="font-medium">
                                        {selectedCompany?.tradingName ||
                                            selectedCompany?.name}
                                    </p>
                                    <p className="text-xs text-base-content/60 mt-1">
                                        {itemsTotal}{' '}
                                        {itemsTotal === 1
                                            ? 'item disponível'
                                            : 'itens disponíveis'}
                                    </p>
                                </div>

                                <p className="text-sm font-medium mb-3">
                                    Como deseja importar?
                                </p>

                                <div className="space-y-2">
                                    <button
                                        type="button"
                                        onClick={() => handleSelectMode('all')}
                                        disabled={importing}
                                        className="flex items-center gap-3 p-4 rounded-lg w-full text-left bg-base-200 hover:bg-secondary/10 hover:border-secondary/30 border border-transparent transition-colors"
                                    >
                                        <div className="p-2 bg-secondary/10 rounded-lg">
                                            <MdSelectAll className="text-xl text-secondary" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">
                                                Importar Todos
                                            </p>
                                            <p className="text-xs text-base-content/60">
                                                Importar todos os {itemsTotal}{' '}
                                                itens
                                            </p>
                                        </div>
                                        {importing && importMode === null && (
                                            <span className="loading loading-spinner loading-sm" />
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleSelectMode('select')
                                        }
                                        disabled={importing}
                                        className="flex items-center gap-3 p-4 rounded-lg w-full text-left bg-base-200 hover:bg-secondary/10 hover:border-secondary/30 border border-transparent transition-colors"
                                    >
                                        <div className="p-2 bg-secondary/10 rounded-lg">
                                            <MdChecklist className="text-xl text-secondary" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">
                                                Selecionar Itens
                                            </p>
                                            <p className="text-xs text-base-content/60">
                                                Escolher quais itens importar
                                            </p>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleSelectMode('search')
                                        }
                                        disabled={importing}
                                        className="flex items-center gap-3 p-4 rounded-lg w-full text-left bg-base-200 hover:bg-secondary/10 hover:border-secondary/30 border border-transparent transition-colors"
                                    >
                                        <div className="p-2 bg-accent/10 rounded-lg">
                                            <MdSearch className="text-xl text-accent" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">
                                                Buscar e Importar
                                            </p>
                                            <p className="text-xs text-base-content/60">
                                                Buscar itens pelo nome para
                                                importar
                                            </p>
                                        </div>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {step === 'select' && (
                    <div className="flex-1 overflow-hidden flex flex-col">
                        {(importMode === 'search' || isPaginated) && (
                            <div className="relative mb-3">
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-lg pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder={`Buscar ${moduleLabel.toLowerCase()}...`}
                                    className="input input-bordered input-sm w-full pl-9"
                                    value={searchTerm}
                                    onChange={e =>
                                        setSearchTerm(e.target.value)
                                    }
                                    autoFocus={importMode === 'search'}
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <MdClose className="text-sm" />
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="flex items-center justify-between mb-2 gap-2">
                            <p className="text-xs text-base-content/60">
                                {isPaginated
                                    ? `${items.length} de ${itemsTotal} ${itemsTotal === 1 ? 'item' : 'itens'}`
                                    : `${filteredItems.length} itens`}
                                {selectedCount > 0 && (
                                    <span className="ml-1 font-medium text-secondary">
                                        ({selectedCount}{' '}
                                        {selectedCount === 1
                                            ? 'selecionado'
                                            : 'selecionados'}
                                        )
                                    </span>
                                )}
                            </p>
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs"
                                onClick={handleToggleAll}
                            >
                                {filteredItems.length > 0 &&
                                filteredItems.every(item =>
                                    selectedIds.includes(getItemId(item))
                                )
                                    ? 'Desmarcar visíveis'
                                    : 'Selecionar visíveis'}
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-1">
                            {loadingItems ? (
                                <div className="flex flex-col items-center justify-center py-12 gap-3">
                                    <span className="loading loading-spinner loading-md text-secondary" />
                                </div>
                            ) : filteredItems.length === 0 ? (
                                <div className="text-center py-6 text-base-content/60">
                                    <p className="text-sm">
                                        Nenhum item encontrado
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {filteredItems.map(item => {
                                        const id = getItemId(item)
                                        const isSelected =
                                            selectedIds.includes(id)
                                        return (
                                            <label
                                                key={id}
                                                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                                                    isSelected
                                                        ? 'bg-secondary/10 border border-secondary/30'
                                                        : 'bg-base-200 hover:bg-base-300 border border-transparent'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-secondary checkbox-sm"
                                                    checked={isSelected}
                                                    onChange={() =>
                                                        handleToggleItem(id)
                                                    }
                                                />
                                                <div className="flex-1 min-w-0">
                                                    {renderItem(item)}
                                                </div>
                                            </label>
                                        )
                                    })}
                                    {isPaginated && hasMore && (
                                        <div className="flex justify-center pt-2">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm gap-2"
                                                onClick={handleLoadMore}
                                                disabled={loadingMore}
                                            >
                                                {loadingMore ? (
                                                    <span className="loading loading-spinner loading-xs" />
                                                ) : (
                                                    <>Carregar mais</>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {step === 'result' && result && (
                    <div className="flex-1 overflow-y-auto">
                        <div
                            className={`alert ${
                                result.success
                                    ? 'alert-success'
                                    : 'alert-warning'
                            } mb-4`}
                        >
                            {result.success ? (
                                <MdCheck className="text-xl" />
                            ) : (
                                <MdError className="text-xl" />
                            )}
                            <span>
                                {result.success
                                    ? `${successCount} ${successCount === 1 ? 'item importado' : 'itens importados'} com sucesso!`
                                    : `${successCount} importados, ${errorCount} falharam.`}
                            </span>
                        </div>

                        {errorCount > 0 && (
                            <div className="space-y-2">
                                {result.results
                                    .filter(r => !r.success)
                                    .map((r, idx) => (
                                        <div
                                            key={idx}
                                            className="flex items-center gap-3 p-3 rounded-lg bg-error/10"
                                        >
                                            <MdError className="text-error text-lg flex-shrink-0" />
                                            <p className="text-xs text-error">
                                                {r.error}
                                            </p>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="modal-action mt-4">
                    {step === 'result' ? (
                        <button
                            className="btn btn-secondary"
                            onClick={handleClose}
                        >
                            Fechar
                        </button>
                    ) : (
                        <>
                            {step !== 'source' && (
                                <button
                                    className="btn btn-ghost gap-1"
                                    onClick={handleBack}
                                    disabled={importing}
                                >
                                    <MdArrowBack className="text-lg" />
                                    Voltar
                                </button>
                            )}

                            <div className="flex-1" />

                            <button
                                className="btn btn-ghost"
                                onClick={handleClose}
                                disabled={importing}
                            >
                                Cancelar
                            </button>

                            {step === 'select' && (
                                <button
                                    className="btn btn-secondary gap-2"
                                    onClick={handleImportSelected}
                                    disabled={selectedCount === 0 || importing}
                                >
                                    {importing ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdFileDownload />
                                    )}
                                    Importar ({selectedCount})
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
            <div className="modal-backdrop" onClick={handleClose}>
                <button className="cursor-default">close</button>
            </div>
        </div>
    )
}
